
import { defineComponent, ref, onMounted, computed } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import Swal from "sweetalert2/dist/sweetalert2.min.js"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"

const CATEGORY_OPTIONS = [
  { value: 'shipping',             label: '快递' },
  { value: 'channel_fee',          label: '支付通道费' },
  { value: 'packaging',            label: '包材' },
  { value: 'server',               label: '服务器/云' },
  { value: 'salary',               label: '人工' },
  { value: 'rent',                 label: '房租' },
  { value: 'utility',              label: '水电网' },
  { value: 'marketing',            label: '营销' },
  { value: 'procurement_premium',  label: '商品采购溢价' },
  { value: 'tax',                  label: '税费' },
  { value: 'other',                label: '其他' },
]
const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map(o => [o.value, o.label]))

export default defineComponent({
  setup() {
    onMounted(() => {
      setCurrentPageBreadcrumbs('运营成本台账', ['财务管理;operating_costs'])
    })

    const store = useStore()
    const tableData = ref<any[]>([])

    const formData = ref({ category: '', from: '', to: '' })
    const page = ref({ currentPage: 1, pageSize: 20, totalResult: 0 })
    const duration = ref<any>(null)

    const dialogVisible = ref(false)
    const dialog = ref<any>({ id: null, category: '', amount: null, cost_date: '', note: '' })
    const dialogTitle = computed(() => dialog.value.id ? '编辑成本' : '新增成本')

    // 月度汇总
    const today = new Date()
    const summaryMonth = ref(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`)
    const summary = ref<any>({ profit_sum: 0, total_cost: 0, net_profit: 0, by_category: {} })
    const byCategoryEntries = computed(() => Object.entries(summary.value.by_category || {}))

    const query = () => {
      const params: any = { ...formData.value, currentPage: page.value.currentPage, pageSize: page.value.pageSize }
      if (duration.value && duration.value.length === 2) {
        params.from = duration.value[0]
        params.to = duration.value[1]
      }
      store.dispatch(Actions.GET_OPERATING_COSTS, params).then(() => {
        tableData.value = store.getters.currentOperatingCosts
        page.value.totalResult = store.getters.currentOperatingCostCount
      })
    }

    const refreshSummary = () => {
      if (!summaryMonth.value) return
      const [y, m] = summaryMonth.value.split('-').map(Number)
      const from = `${y}-${String(m).padStart(2, '0')}-01`
      const last = new Date(y, m, 0).getDate()
      const to = `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`
      store.dispatch(Actions.GET_OPERATING_COST_MONTHLY_SUMMARY, { from, to }).then(() => {
        summary.value = store.getters.currentOperatingCostMonthlySummary
      })
    }

    query()
    refreshSummary()

    const onSearch = () => { page.value.currentPage = 1; query() }
    const onReset = () => {
      formData.value = { category: '', from: '', to: '' }
      duration.value = null
      page.value.currentPage = 1
      query()
    }
    const onPageChange = (e) => {
      page.value.currentPage = e.currentPage
      page.value.pageSize = e.pageSize
      query()
    }

    const onNew = () => {
      dialog.value = { id: null, category: '', amount: null, cost_date: '', note: '' }
      dialogVisible.value = true
    }
    const onEdit = (row) => {
      dialog.value = { id: row.id, category: row.category, amount: Number(row.amount), cost_date: row.cost_date, note: row.note || '' }
      dialogVisible.value = true
    }
    const onDialogClose = () => { /* noop */ }

    const onSave = () => {
      if (!dialog.value.category) { Swal.fire({ text: '请选择分类', icon: 'warning' }); return }
      if (dialog.value.amount === null || dialog.value.amount === undefined) { Swal.fire({ text: '请输入金额', icon: 'warning' }); return }
      if (Number(dialog.value.amount) >= 0) { Swal.fire({ text: '金额必须为负数（成本是流出）', icon: 'warning' }); return }
      if (!dialog.value.cost_date) { Swal.fire({ text: '请选择成本日期', icon: 'warning' }); return }

      const action = dialog.value.id ? Actions.UPDATE_OPERATING_COST : Actions.CREATE_OPERATING_COST
      const params = { ...dialog.value }
      store.dispatch(action, params)
        .then(() => {
          dialogVisible.value = false
          Swal.fire({ text: '保存成功', icon: 'success', timer: 1200, showConfirmButton: false })
          query()
          refreshSummary()
        })
        .catch((e) => {
          const msg = (e && e.error_msg) || store.getters.getErrors || '保存失败'
          Swal.fire({ text: typeof msg === 'string' ? msg : JSON.stringify(msg), icon: 'error' })
        })
    }

    const onDelete = (row) => {
      Swal.fire({
        title: '确认删除该笔成本？',
        showCancelButton: true,
        confirmButtonText: '确认',
        cancelButtonText: '取消'
      }).then((r) => {
        if (!r.isConfirmed) return
        store.dispatch(Actions.DELETE_OPERATING_COST, row.id).then(() => {
          query()
          refreshSummary()
        })
      })
    }

    const categoryLabel = (v: string) => CATEGORY_LABEL[v] || v
    const formatMoney = (n: any) => {
      const num = Number(n || 0)
      const sign = num < 0 ? '-' : ''
      const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${sign}¥${abs}`
    }
    const valueColor = (n: any) => Number(n) < 0 ? 'text-danger' : 'text-success'

    return {
      tableData, formData, page, duration,
      summary, summaryMonth, byCategoryEntries,
      dialog, dialogVisible, dialogTitle,
      categoryOptions: CATEGORY_OPTIONS,
      onSearch, onReset, onPageChange, onNew, onEdit, onSave, onDelete,
      onDialogClose, refreshSummary,
      categoryLabel, formatMoney, valueColor,
    }
  },
})
